import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Stack,
  useBreakpointValue,
  HStack,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { House, Storefront, VideoCamera, UsersFour } from "phosphor-react";

const MotionBox = motion(Box);

const Hero = () => {
  const triangleSize = useBreakpointValue({
    base: "150px",
    md: "200px",
    lg: "300px",
  });
  return (
    <Box position="relative" minHeight="100vh" overflow="hidden">
      {/* Background Video */}
      <Box
        as="video"
        autoPlay
        muted
        loop
        playsInline
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        objectFit="cover"
        zIndex="0"
      >
        <source src="/Golf_Simulator.mp4" type="video/mp4" />
      </Box>

      {/* Dark Overlay */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        background="radial-gradient(circle at center, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.9) 100%)"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(45deg, rgba(0,0,0,0.95) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.95) 100%)",
          pointerEvents: "none",
        }}
        zIndex="1"
      />

      {/* Animated triangles in background */}
      <MotionBox
        position="absolute"
        top="-50px"
        right="-50px"
        width={triangleSize}
        height={triangleSize}
        borderLeft="2px solid"
        borderColor="oxygon.primary"
        transform="rotate(45deg)"
        opacity={0.1}
        animate={{
          rotate: [45, 90, 45],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear",
        }}
        zIndex="2"
      />
      <MotionBox
        position="absolute"
        bottom="-50px"
        left="-50px"
        width={triangleSize}
        height={triangleSize}
        borderRight="2px solid"
        borderColor="oxygon.secondary"
        transform="rotate(-45deg)"
        opacity={0.1}
        animate={{
          rotate: [-45, -90, -45],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear",
        }}
        zIndex="2"
      />

      <Container
        maxW="container.xl"
        height="100%"
        position="relative"
        zIndex="2"
      >
        <Stack
          spacing={8}
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="100vh"
          py={{ base: 20, md: 0 }}
          data-aos="fade-up"
        >
          <Heading
            as="h1"
            size="4xl"
            bgGradient="linear(to-r, oxygon.primary, oxygon.secondary)"
            bgClip="text"
            letterSpacing="tight"
            p={2.5}
          >
            <Box display="block" textAlign="center">
              <Box
                as="img"
                src="/logo_icon.svg"
                alt="Virtual Golf Society"
                display="block"
                boxSize="2.0em"
                mx="auto"
                mb="0.0em"
                draggable={false}
              />
              <Box display="block">Virtual Golf</Box>
              <Box display="block">Society</Box>
            </Box>
          </Heading>
          <Text fontSize="xl" maxW="800px" color="gray.300">
            Track every sim round, share your golf life, and compete in tours,
            tournaments, and club events – all in one app.
          </Text>

          {/* Categories Row */}
          <HStack
            spacing={{ base: 4, md: 8 }}
            justify="center"
            py={4}
            divider={
              <Divider
                orientation="vertical"
                height="20px"
                borderColor="oxygon.primary"
                opacity={0.3}
              />
            }
          >
            <Stack align="center" spacing={1}>
              <Icon as={House} w={6} h={6} color="oxygon.primary" />
              <Text color="gray.300" fontSize="sm" fontWeight="medium">
                Home sim owners
              </Text>
            </Stack>
            <Stack align="center" spacing={1}>
              <Icon as={Storefront} w={6} h={6} color="oxygon.primary" />
              <Text color="gray.300" fontSize="sm" fontWeight="medium">
                Venues & schools
              </Text>
            </Stack>
            <Stack align="center" spacing={1}>
              <Icon as={VideoCamera} w={6} h={6} color="oxygon.primary" />
              <Text color="gray.300" fontSize="sm" fontWeight="medium">
                Creators & communities
              </Text>
            </Stack>
            <Stack align="center" spacing={1}>
              <Icon as={UsersFour} w={6} h={6} color="oxygon.primary" />
              <Text color="gray.300" fontSize="sm" fontWeight="medium">
                Friend groups
              </Text>
            </Stack>
          </HStack>

          <Stack direction={{ base: "column", md: "row" }} spacing={4}>
            <Button
              as="a"
              href="https://app.joinvgs.com/login?mode=signup"
              size="lg"
              bg="white"
              color="gray.900"
              position="relative"
              zIndex={2}
              _hover={{
                bg: "gray.100",
              }}
            >
              Create Free Account
            </Button>
            <Button
              as="a"
              href="https://app.joinvgs.com/login"
              variant="secondary"
              size="lg"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 4px 20px rgba(112, 0, 255, 0.3)",
              }}
            >
              Log In
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Hero;
